import type { ChangeEventHandler } from 'react'
import React, { useEffect, useMemo, useRef } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import IconButton from '../IconButton/IconButton'
import ChevronLeft from '../icons/ChevronLeft'
import ChevronRight from '../icons/ChevronRight'
import Select from '../Select/Select'
import Skeleton from '../Skeleton/Skeleton'
import Typography from '../Typography/Typography'
import { ActionTypes } from './Actions'
import { useTable } from './TableContext'
import type { Limit, Pagination, TableState } from './TableContext'

export type HandlesPaginationChange = (args: {
  limit: Limit
  page: number
}) => void

interface PaginationProps {
  /**
   * Callback function invoked when pagination state changes
   */
  onChange: HandlesPaginationChange
}

interface PaginationMeta {
  end: number
  hasNext: boolean
  hasPrev: boolean
  pagination: Pagination
  start: number
  total?: number
}

const computeRowStart = (state: Pick<TableState, 'limit' | 'page' | 'rows'>) =>
  state.rows.length > 0 ? (state.page - 1) * state.limit + 1 : 0

const computeRowEnd = (start: number, count: number) =>
  count > 0 ? start + count - 1 : 0

/**
 * Determine if the current state has a sentinel row (i.e., more rows than the limit)
 * indicating more data is available.
 */
const hasSentinel = (state: Pick<TableState, 'limit' | 'rows'>) =>
  state.rows.length > state.limit

const buildEmptyMeta = (
  state: Pick<TableState, 'pagination'>,
): PaginationMeta => ({
  end: 0,
  hasNext: false,
  hasPrev: false,
  pagination: state.pagination,
  start: 0,
  total: 0,
})

const buildSimpleMeta = (
  state: Pick<TableState, 'limit' | 'page' | 'rows'>,
): PaginationMeta => {
  const start = computeRowStart(state)
  const sentinel = hasSentinel(state)
  const hasPrev = start > 1
  // We return one extra row as a sentinel to indicate more data is available so this check is necessary.
  const visibleCount = sentinel ? state.limit : state.rows.length
  const end = computeRowEnd(start, visibleCount)
  const total = sentinel ? undefined : end

  return {
    end,
    hasNext: sentinel,
    hasPrev,
    pagination: 'simple',
    start,
    total,
  }
}

const buildLengthAwareMeta = (
  state: Pick<TableState, 'limit' | 'page' | 'rows' | 'total'>,
): PaginationMeta => {
  const start = computeRowStart(state)
  const hasPrev = start > 1
  const hasNext = state.page * state.limit < (state.total as number)
  const remaining = (state.total as number) - start + 1
  const visibleCount = Math.min(state.limit, remaining)
  const end = computeRowEnd(start, visibleCount)

  return {
    end,
    hasNext,
    hasPrev,
    pagination: 'lengthAware',
    start,
    total: state.total,
  }
}

const computePaginationMeta = (
  state: Pick<TableState, 'limit' | 'pagination' | 'page' | 'rows' | 'total'>,
): PaginationMeta => {
  if (state.rows.length === 0) {
    return buildEmptyMeta(state)
  }

  return state.pagination === 'simple'
    ? buildSimpleMeta(state)
    : buildLengthAwareMeta(state)
}

function Position({
  loading,
  meta,
}: {
  loading: boolean
  meta: PaginationMeta
}): JSX.Element {
  const intl = useIntl()

  if (loading) {
    return (
      <span className="inline-flex w-24 align-middle">
        <Skeleton
          aria-label={intl.formatMessage({ id: 'Table.pagination_loading' })}
          color="light"
          variant="square"
          width="long"
        />
      </span>
    )
  }

  if (meta.start === 0 && meta.end === 0) {
    return (
      <Typography component="p" variant="subtitle2">
        {intl.formatMessage({ id: 'Ui.no_results' })}
      </Typography>
    )
  }

  return (
    <Typography component="p" variant="subtitle2">
      {intl.formatMessage(
        { id: 'Table.position' },
        {
          end: meta.end,
          start: meta.start,
          total: meta.total || `${meta.end + 1}+`,
        },
      )}
    </Typography>
  )
}

function PaginationComponent({ onChange }: PaginationProps): JSX.Element {
  const intl = useIntl()
  const [state, dispatch] = useTable()
  const didMountRef = useRef(false)

  const meta = useMemo(
    () =>
      computePaginationMeta({
        limit: state.limit,
        page: state.page,
        pagination: state.pagination,
        rows: state.rows,
        total: state.total,
      }),
    [state.limit, state.page, state.pagination, state.rows, state.total],
  )

  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true

      return // skip initial mount
    }

    // Intentionally not calling onChange on every render; only when limit/page change.
    // onChange is omitted from deps because parent does not provide a stable callback.
    onChange({ limit: state.limit, page: state.page })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.limit, state.page])

  const handleNavigate = (nextPage: number) => {
    if (nextPage < 1 || nextPage === state.page) {
      return
    }

    dispatch({
      type:
        nextPage > state.page ? ActionTypes.NextPage : ActionTypes.PreviousPage,
    })
  }

  const handleClickNext = () => {
    if (meta.hasNext) {
      handleNavigate(state.page + 1)
    }
  }

  const handleClickPrevious = () => {
    if (meta.hasPrev) {
      handleNavigate(state.page - 1)
    }
  }

  const handleChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    const nextLimit = Number(e.currentTarget.value) as Limit

    dispatch({ type: ActionTypes.ChangeLimit, limit: nextLimit })
  }

  if (state.rows.length === 0) {
    return (
      <Typography component="p" variant="subtitle2">
        <FormattedMessage id="Ui.no_results" />
      </Typography>
    )
  }

  return (
    <div className="flex items-center">
      <nav
        aria-label={intl.formatMessage({ id: 'Table.pagination' })}
        className="flex items-center"
      >
        <Position loading={state.loading} meta={meta} />
        <ul className="flex items-center ml-4">
          <li className="inline-flex">
            <IconButton
              aria-label={intl.formatMessage({ id: 'Pagination.previous' })}
              disabled={!meta.hasPrev || state.loading}
              onClick={handleClickPrevious}
            >
              <ChevronLeft />
            </IconButton>
          </li>
          <li className="inline-flex ml-3">
            <IconButton
              aria-label={intl.formatMessage({ id: 'Pagination.next' })}
              disabled={!meta.hasNext || state.loading}
              onClick={handleClickNext}
            >
              <ChevronRight />
            </IconButton>
          </li>
        </ul>
        <div className="ml-6 min-w-40">
          <Select
            defaultValue={String(state.limit)}
            disabled={state.loading}
            label={intl.formatMessage({ id: 'Table.per_page' })}
            onChange={handleChange}
            size="small"
          >
            <Select.Option value="25">25</Select.Option>
            <Select.Option value="50">50</Select.Option>
            <Select.Option value="100">100</Select.Option>
          </Select>
        </div>
      </nav>
    </div>
  )
}

export default PaginationComponent
